/**
 * LEA - Logical Engineering Assistant
 * Main entry point for Excel Add-in
 * 
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 */

/* global document, Excel, Office */

// Utils
import { appendMessage } from "../utils/helpers.js";
import { debugError } from "../config/debugConfig.js";
import { isExcelHost, setExcelHost } from "../config/hostConfig.js";

// Services
import {
  initializeMsal,
  checkExistingSession,
  loadUserInfo,
  login,
  logout,
  getCurrentUserEmail,
  getMsalInstance,
  getAccount
} from "../services/authService.js";

import {
  initializeOpenAIService,
  clearOpenAIService,
  getOpenAIService,
  sendMessage
} from "../services/chatService.js";

import {
  displayQuota,
  updateEstimatedUsage,
  clearQuota
} from "../services/quotaService.js";

// UI
import {
  loadBackgroundInfo,
  saveBackgroundInfo,
  reloadBackgroundDialog,
  loadBackgroundFromRow4,
  exportBackgroundToRow4,
  initBackgroundTab
} from "../ui/backgroundDialog.js";

import {
  loadMode,
  loadModel,
  toggleMode,
  changeModel,
  getCurrentModel,
  updateVersionDisplay,
  clearAddinCache,
  openHelpEmail,
  setOpenAIServiceRef
} from "../ui/settingsUI.js";

import {
  initRAGUI,
  openRAGDialog,
  closeRAGDialog,
  handleRAGToggleChange,
  handleConnectSharePoint,
  handleSaveSharePointConfig,
  handleDisconnectSharePoint,
  handleChangeSharePointConfig,
  handleBrowseFolder
} from "../ui/ragDialog.js";

import { initEpjEditUI } from "../ui/epjEditDialog.js";
// Confirmation dialog state
let confirmCallback = null;

Office.onReady(async (info) => {
  setExcelHost(info.host === Office.HostType.Excel);

  // Initialize in both Excel and standalone browser mode
  {
    try {
      // Update version display
      updateVersionDisplay();
      
      // Initialize MSAL
      await initializeMsal();
      
      // Check if user is already signed in
      const sessionResult = await checkExistingSession();
      if (sessionResult === true) {
        await loadUserInfo(updateUserDisplay, updateUI);
      } else if (sessionResult && sessionResult.restored) {
        updateUserDisplay(sessionResult.userName, sessionResult.email);
        updateUI(true);
      } else {
        updateUI(false);
      }

      // Set up UI
      document.getElementById("app-body").classList.remove("hidden");
      document.getElementById("auth-ui").classList.remove("hidden");
      
      // Set up event handlers
      setupEventHandlers();
      
      // Load saved settings
      loadBackgroundInfo();
      loadMode();
      loadModel();
      
      // Set up worksheet change listener (Excel only)
      if (isExcelHost()) {
        setupWorksheetChangeListener();
      }
      
    } catch (error) {
      debugError("Error initializing:", error);
    }
  }
});

function setupEventHandlers() {
  // Auth handlers
  document.getElementById("login").onclick = handleLogin;
  document.getElementById("logout").onclick = handleLogout;
  
  // Chat handlers
  document.getElementById("send-message").onclick = sendMessage;
  
  // Background info handlers (now in RAG dialog)
  document.getElementById("save-background").onclick = saveBackgroundInfo;
  document.getElementById("load-from-row4").onclick = () => loadBackgroundFromRow4(showConfirmDialog);
  document.getElementById("export-to-row4").onclick = () => exportBackgroundToRow4(showConfirmDialog);
  
  // Hide Excel-only buttons in standalone mode
  if (!isExcelHost()) {
    document.getElementById("load-from-row4").style.display = "none";
    document.getElementById("export-to-row4").style.display = "none";
  }
  
  // Confirmation dialog handlers
  document.getElementById("confirm-yes").onclick = confirmYes;
  document.getElementById("confirm-no").onclick = confirmNo;
  
  // RAG dialog handlers
  const ragBtn = document.getElementById("rag-btn");
  if (ragBtn) ragBtn.onclick = openRAGDialog;
  
  const closeRagDialogBtn = document.getElementById("close-rag-dialog");
  if (closeRagDialogBtn) closeRagDialogBtn.onclick = closeRAGDialog;
  
  const closeRagBtn = document.getElementById("close-rag-btn");
  if (closeRagBtn) closeRagBtn.onclick = closeRAGDialog;
  
  // SharePoint connection handlers
  const connectSharePointBtn = document.getElementById("connect-sharepoint-btn");
  if (connectSharePointBtn) connectSharePointBtn.onclick = handleConnectSharePoint;
  
  const saveSharePointConfigBtn = document.getElementById("save-sharepoint-config");
  if (saveSharePointConfigBtn) saveSharePointConfigBtn.onclick = handleSaveSharePointConfig;
  
  const disconnectSharePointBtn = document.getElementById("disconnect-sharepoint");
  if (disconnectSharePointBtn) disconnectSharePointBtn.onclick = handleDisconnectSharePoint;
  
  const changeSharePointConfigBtn = document.getElementById("change-sharepoint-config");
  if (changeSharePointConfigBtn) changeSharePointConfigBtn.onclick = handleChangeSharePointConfig;
  
  const browseFolderBtn = document.getElementById("browse-folder-btn");
  if (browseFolderBtn) browseFolderBtn.onclick = handleBrowseFolder;
  
  // RAG context toggle handler
  const ragContextToggle = document.getElementById("rag-context-toggle");
  if (ragContextToggle) ragContextToggle.addEventListener("change", handleRAGToggleChange);
  
  // Mode toggle handler
  document.getElementById("mode-toggle").addEventListener("change", toggleMode);
  
  // Model selector handler
  document.getElementById("model-select").addEventListener("change", changeModel);
  
  // Version click handler for clearing cache
  document.getElementById("app-version").onclick = () => clearAddinCache(appendMessage);
  
  // Help button handler
  document.getElementById("help").onclick = () => openHelpEmail(getCurrentUserEmail());
  
  // Chat input handling
  const chatInput = document.getElementById("chat-input");
  chatInput.addEventListener("keydown", (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  });
  
  // Update estimated usage on input change
  chatInput.addEventListener("input", updateEstimatedUsage);
}

// Auth handlers
async function handleLogin() {
  await login(updateUserDisplay, updateUI);
}

async function handleLogout() {
  await logout(updateUI, () => {
    clearOpenAIService();
    clearQuota();
    setOpenAIServiceRef(null);
  });
}

// UI update functions
function updateUserDisplay(name, email) {
  document.getElementById("user-name").textContent = name;
  document.getElementById("user-info").classList.remove("hidden");
}

function updateUI(isLoggedIn) {
  const loginButton = document.getElementById("login");
  const logoutButton = document.getElementById("logout");
  const userInfo = document.getElementById("user-info");
  const chatInterface = document.getElementById("chat-interface");
  
  if (isLoggedIn) {
    loginButton.classList.add("hidden");
    logoutButton.classList.remove("hidden");
    userInfo.classList.remove("hidden");
    continueWithoutKey();
  } else {
    loginButton.classList.remove("hidden");
    logoutButton.classList.add("hidden");
    userInfo.classList.add("hidden");
    chatInterface.classList.add("hidden");
    showLoginPrompt();
  }
}

function showLoginPrompt() {
  const appBody = document.getElementById("app-body");
  const chatInterface = document.getElementById("chat-interface");
  let loginPrompt = document.getElementById("login-prompt");
  
  if (!loginPrompt) {
    loginPrompt = document.createElement("div");
    loginPrompt.id = "login-prompt";
    loginPrompt.className = "login-prompt";
    loginPrompt.innerHTML = "<p>Bitte melde dich mit deiner E-Mail-Adresse an, um den Intelligenten Daten Assistenten zu nutzen.</p>";
    appBody.insertBefore(loginPrompt, chatInterface);
  }
  loginPrompt.classList.remove("hidden");
}

async function continueWithoutKey() {
  try {
    const currentUserEmail = getCurrentUserEmail();
    const currentModel = getCurrentModel();
    
    // Initialize OpenAI service without an API key but with userId
    const service = initializeOpenAIService(null, currentUserEmail);
    service.deploymentName = currentModel;
    
    // Set reference for settings UI
    setOpenAIServiceRef(service);
    
    // Hide login prompt if it exists
    const loginPrompt = document.getElementById("login-prompt");
    if (loginPrompt) {
      loginPrompt.classList.add("hidden");
    }
    
    // Show the chat interface
    document.getElementById("chat-interface").classList.remove("hidden");
    
    // Add a welcome message
    const welcomeMsg = isExcelHost()
      ? "👋 Hallo! Ich bin **LEA**, dein **L**ogischer **E**ngineering **A**ssistent. Markiere einfach die Zellen, die du anpassen oder überprüfen möchtest, und sag mir, was zu tun ist 🙂"
      : "👋 Hallo! Ich bin **LEA**, dein **L**ogischer **E**ngineering **A**ssistent. Ich unterstütze dich bei allen Fragen rund um **EPLAN**, **ECAD-Daten** und **Artikeldatenverwaltung** 🙂";
    appendMessage(welcomeMsg, false);
    
    // Load quota first (sets currentQuota), then initialize feature-gated UI
    const msal = getMsalInstance();
    const account = getAccount();
    await displayQuota();
    await initRAGUI(msal, account);
    
    // Initialize EPJ Edit UI (feature-flag gated, depends on quota being loaded)
    initEpjEditUI();

  } catch (error) {
    debugError("Error initializing without API key:", error);
  }
}

// Confirmation dialog functions
function showConfirmDialog(message, callback) {
  confirmCallback = callback;
  document.getElementById("confirm-message").textContent = message;
  document.getElementById("confirm-dialog").classList.remove("hidden");
}

function confirmYes() {
  closeConfirmDialog();
  if (confirmCallback) {
    confirmCallback();
    confirmCallback = null;
  }
}

function confirmNo() {
  closeConfirmDialog();
  confirmCallback = null;
}

function closeConfirmDialog() {
  document.getElementById("confirm-dialog").classList.add("hidden");
}

// Worksheet change listener
function setupWorksheetChangeListener() {
  Excel.run(async (context) => {
    const worksheet = context.workbook.worksheets.getActiveWorksheet();
    worksheet.onSelectionChanged.add(onSelectionChanged);
    await context.sync();
  }).catch(error => {
    debugError("Error setting up worksheet selection change listener:", error);
  });
}

function onSelectionChanged(event) {
  // Update estimated usage when selection changes
  updateEstimatedUsage();

  // Check if RAG dialog is open and background tab is active
  const ragDialog = document.getElementById("rag-dialog");
  const backgroundTab = document.getElementById("background-tab");
  if (ragDialog && !ragDialog.classList.contains("hidden") && 
      backgroundTab && backgroundTab.classList.contains("active")) {
    reloadBackgroundDialog();
  }
}

// Export run function for compatibility (Excel only)
export async function run() {
  if (!isExcelHost()) return;
  
  const currentUserEmail = getCurrentUserEmail();
  
  if (!currentUserEmail) {
    await handleLogin();
    if (!getCurrentUserEmail()) {
      return;
    }
  }

  try {
    await Excel.run(async (context) => {
      const range = context.workbook.getSelectedRange();
      range.load("address");
      range.format.fill.color = "yellow";
      await context.sync();
      debugLog(`The range address was ${range.address}.`);
    });
  } catch (error) {
    debugError(error);
  }
}